import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import DefaultMap from '@/pages/notification/bio/DefaultMap.tsx'
import { format } from 'date-fns'
import ContactBadge from '@/component/badge/ContactBadge.tsx'
import { equalString, isEmpty } from '@/util/strings.ts'
import Input from '@/component/input/Input.tsx'
import Select, { ISelectOption } from '@/component/input/Select.tsx'
import BorderButton from '@/component/button/BorderButton.tsx'
import SolidButton from '@/component/button/SolidButton.tsx'
import { apiPatchBioAlertAssignPerson, apiPatchBioAlertProcess } from '@/api/notification/patchBioAlert.ts'
import useQueryGetAlertBioDetail from '@/api/notification/getAlertBioDetail.ts'
import useQueryGetUserDetail from '@/api/auth/getUser.ts'
import useQueryGetMe from '@/api/auth/getMe.ts'
import { useNavigate } from 'react-router-dom'
import { BioAlertModalContext } from '@/pages/notification/bio/BioAlertModal.tsx'
import { BioAlertConfirmStatus } from '@/api/notification/getNotificationBio.ts'
import { useTranslation } from 'react-i18next'

export interface IProps {
    className?: string
}

const BioAlertDetail: React.FC<IProps> = ({ className }) => {
    const { t } = useTranslation()

    const { id, refetchList, refetchAndResetSelection } = useContext(BioAlertModalContext)
    const { data, refetch: refetchDetail } = useQueryGetAlertBioDetail(id)
    const { data: user } = useQueryGetUserDetail(data?.protectee?.id)
    const { data: me } = useQueryGetMe()
    const [isExpandedMap, setExpandedMap] = useState(false)
    const navigate = useNavigate()
    
    // 특정 이메일 계정인지 확인
    const isMaskingRequired = useMemo(() => {
        return me?.email === 'dbd_cm@clomental.com'
    }, [me?.email])
    
    // 마스킹 처리 함수
    const getMaskedName = (name?: string) => {
        if (!isMaskingRequired || !name) return name
        if (name.length <= 1) return name
        return name.charAt(0) + '**'
    }
    
    const getMaskedContact = (contact?: string) => {
        if (!isMaskingRequired || !contact) return contact
        
        // 모든 전화번호 앞 3자리만 보여주고 나머지는 * 처리
        const contactParts = contact.split('-')
        if (contactParts.length === 3) {
            return `${contactParts[0]}-****-****`
        }
        
        // 기타 패턴의 전화번호 처리
        if (contact.length > 3) {
            return contact.substring(0, 3) + contact.substring(3).replace(/./g, '*')
        }
        
        return contact
    }
    
    const getMaskedAddress = (address?: string) => {
        if (!isMaskingRequired || !address) return address
        return '****'
    }

    const actionTypes: ISelectOption[] = useMemo(
        () => [
            {
                id: '기타',
                text: t('notification.bioModal.type.etc'),
                value: '기타'
            },
            {
                id: '119출동',
                text: t('notification.bioModal.type.119'),
                value: '119출동'
            },
            {
                id: '오작동',
                text: t('notification.bioModal.type.malfunction'),
                value: '오작동'

            }
        ],
        []
    )

    const [manager, setManager] = useState('')
    const [actionDetail, setActionDetail] = useState('')
    const [actionTypeId, setActionTypeId] = useState('기타')


    const onClickAssign = async () => {
        if (!id) return

        const { code } = await apiPatchBioAlertAssignPerson(id, { manager_name: manager })
        if (code == 200) {
            await refetchDetail()
            refetchList()
        }
    }

    const getGenderName = useCallback((value: string) => {
        switch (value) {
            case '남':
                return t('notification.bioModal.gender.male')
            case '여':
                return t('notification.bioModal.gender.female')
            default:
                return value
        }
    }, [t])

    const onClickProcessAction = async () => {
        if (!id) return
        if (!actionTypeId) return

        const { code } = await apiPatchBioAlertProcess(id, {
            task_result: actionTypeId,
            task_content: actionDetail
        })
        if (code == 200) {
            await refetchDetail()
            refetchAndResetSelection()
        }
    }

    useEffect(() => {
        setActionTypeId(data?.alert?.result || '기타')
        setActionDetail(data?.alert?.content || '')
        setManager(me?.name || '')

    }, [data, me])

    const canEditContent = useMemo(() => {
        const confirmStatus = data?.alert?.confirm

        switch (confirmStatus) {
            case BioAlertConfirmStatus.DONE:
                return true
            case BioAlertConfirmStatus.ASSIGN:
                return !!data?.alert?.isMyTask
            case  BioAlertConfirmStatus.WAIT:
            default:
                return false
        }
    }, [data])


    return <div className={`${className}`}>
        <div
            className={'flex flex-col border border-solid border-transparent border-l-gray2 py-[20px] px-[5px]'}>
            <div
                className={'grid grid-cols-2 gap-[10px]'}>
                <div
                    className={'flex flex-col justify-center gap-[10px] p-[12px]'}>
                    
                    {/* Event Info & Device Status */}
                    <div className={'flex flex-col gap-[10px] mb-[10px]'}>
                        <div className={'bg-[#FFF4F4] p-[15px] rounded-[5px] border border-sub_r'}>
                            <p className={'text-[18px] font-bold text-sub_r mb-[5px]'}>
                                {(data?.alert?.nameDetail as any)?.eventLabel ||
                                 (data?.alert?.nameDetail?.hrateStatus && !equalString(data?.alert?.nameDetail?.hrateStatus, '정상') ? t('dashboard.notification.signal_unstable') :
                                 ((data?.alert?.nameDetail?.respStatus && !equalString(data?.alert?.nameDetail?.respStatus, '정상')) || (data?.alert?.nameDetail?.resp !== undefined && data?.alert?.nameDetail?.resp >= 22) ? t('dashboard.notification.wear_check') : t('users.detail.status.check_required')))}
                            </p>
                            <p className={'text-[14px] text-gray-600'}>
                                {t('notification.alert.time')}: {data?.alert?.createdAt && format(data?.alert?.createdAt, 'yyyy-MM-dd HH:mm:ss')}
                            </p>
                        </div>
                        
                        <div className={'bg-gray-50 p-[15px] rounded-[5px] border border-gray2'}>
                            <p className={'font-bold mb-[8px] text-[15px]'}>{t('users.detail.location.device.status')}</p>
                            <div className={'grid grid-cols-2 gap-y-[5px] text-[14px]'}>
                                <div className={'flex items-center gap-[5px]'}>
                                    <span className={'text-gray-500'}>{t('mentalHealth.device.status.desc')}:</span>
                                    <span className={(data?.alert?.nameDetail as any)?.isOnline ? 'text-green-600 font-medium' : 'text-gray-400'}>
                                        {(data?.alert?.nameDetail as any)?.isOnline ? t('mentalHealth.device.status.online') : t('mentalHealth.device.status.offline')}
                                    </span>
                                </div>
                                <div className={'flex items-center gap-[5px]'}>
                                    <span className={'text-gray-500'}>{t('mentalHealth.device.battery')}:</span>
                                    <span>{(data?.alert?.nameDetail as any)?.batteryLevel !== undefined ? `${(data?.alert?.nameDetail as any)?.batteryLevel}%` : '-'}</span>
                                </div>
                                <div className={'flex items-center gap-[5px]'}>
                                    <span className={'text-gray-500'}>{t('users.detail.location.device.status.charging')}:</span>
                                    <span>{(data?.alert?.nameDetail as any)?.isCharging ? 'Yes' : 'No'}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        className={'bg-sub_b px-[15px] py-[8px] border border-solid border-main_b rounded-[5px] flex justify-between text-[20px] font-medium text-main_b'}>
                            <span
                                className={'cursor-pointer'}
                                onClick={() => navigate(`/user?id=${data?.protectee?.id}`)}>{getMaskedName(data?.protectee?.name)}</span>
                        <span>{getMaskedContact(data?.protectee?.contact)}</span>
                    </div>
                    <div
                        className={'relative flex flex-col bg-gray1 px-[15px] py-[8px] border border-solid border-gray2 rounded-[5px] gap-[14px] cursor-pointer'}
                        onClick={() => setExpandedMap(!isExpandedMap)}>
                        <div className={'flex justify-between items-center'}>
                            <span>{getMaskedAddress(data?.alert?.location?.address)}</span>
                            <img src="/ic_map_marker.png" />
                        </div>
                        {isExpandedMap && data?.alert?.location && <DefaultMap
                            className={'min-h-[188px]'}
                            latitude={data?.alert?.location?.latitude}
                            longitude={data?.alert?.location?.longitude} />}
                    </div>
                    <p>{t('notification.bioModal.address')} : {getMaskedAddress(user?.address) || t('common.empty')}</p>
                    <p>{getGenderName(data?.protectee?.gender || '')} / {data?.protectee?.birthDate && format(data?.protectee?.birthDate, 'yy-MM-dd')}</p>

                    <p className={'text-left text-[14px] font-bold'}>{t('notification.bioModal.contact.title')}</p>
                    <div className={'grid grid-cols-2 gap-[10px]'}>
                        <div className={'flex gap-[10px]'}>
                            <ContactBadge>{t('notification.bioModal.contact.me')}</ContactBadge>
                            <p>{getMaskedContact(data?.protectee?.contact) || t('common.empty')}</p>
                        </div>
                        <div className={'flex gap-[10px]'}>
                            <ContactBadge>{t('notification.bioModal.contact.protector')}</ContactBadge>
                            <p>{getMaskedContact(data?.protector?.contact) || t('common.empty')}</p>
                        </div>
                        <div className={'flex gap-[10px]'}>
                            <ContactBadge>{t('notification.bioModal.contact.acquaintance')}</ContactBadge>
                            <p>{getMaskedContact(data?.protector?.secondaryContact) || t('common.empty')}</p>
                        </div>
                        <div className={'flex gap-[10px]'}>
                            <ContactBadge>{t('notification.bioModal.contact.admin')}</ContactBadge>
                            <p>{getMaskedContact(data?.protector?.tertiaryContact) || t('common.empty')}</p>
                        </div>
                    </div>

                    <p className={'text-[14px] font-bold mt-[10px]'}>{t('notification.bioModal.memo.title')}</p>
                    <div className={'p-[12px] bg-sub_b min-h-[158px]'}>
                        {isMaskingRequired ? '***************************' : data?.protectee?.memo}
                    </div>
                </div>
                <div className={'flex flex-col p-[12px] gap-[10px]'}>
                    {!equalString(data?.alert?.confirm, '미처리') &&
                        <div className={'flex gap-[13px] items-center'}>
                            <p className={'flex-none'}>{t('notification.bioModal.manager.title')}</p>
                            <Input inputClassName={''} disabled className={'w-full'}
                                   value={getMaskedName(data?.alert?.managerName) || ''} />
                        </div>}
                    <div className={'flex gap-[13px] items-center'}>
                        <p className={'flex-none'}>{t('notification.bioModal.type.title')}</p>
                        <Select className={'w-full'}
                                value={actionTypes?.find(item => item.id == actionTypeId)}
                                options={actionTypes}
                                disabled={!canEditContent}
                                onChanged={option =>
                                    setActionTypeId(option?.id)
                                } />
                    </div>
                    <div
                        className={`flex flex-col p-[12px] border border-solid border-gray2 h-full text-[14px] ${!canEditContent && 'bg-[#f1f1f1]'}`}>
                        <p>{t('notification.bioModal.process.title')} : </p>
                        <textarea className={`border-none w-full h-full resize-none`}
                                  disabled={!canEditContent}
                                  value={actionDetail}
                                  onChange={e => setActionDetail(e.target.value)}
                        />
                    </div>
                </div>
            </div>
            <div className={'mt-[15.5px]'}>
                {/* 하단 버튼들 */}
                {data?.alert?.confirm == BioAlertConfirmStatus.WAIT && <>
                    <div className={'flex gap-[20px] justify-center'}>
                        <input
                            className={'min-w-[370px] py-[13.5px] px-[15px] border border-solid border-sub_r'}
                            placeholder={t('notification.bioModal.manager.input.placeholder')}
                            value={manager}
                            onChange={e => {
                                const text = e.target.value
                                setManager(text)
                            }} />
                        <BorderButton text={t('notification.bioModal.manager.btn.title')}
                                      className={'text-sub_r border-sub_r min-w-[200px]'}
                                      onClick={onClickAssign} />
                    </div>
                </>}
                {data?.alert?.confirm == BioAlertConfirmStatus.ASSIGN && data?.alert?.isMyTask && <>
                    <div className={'flex gap-[20px] justify-center'}>
                        <SolidButton text={t('notification.bioModal.submit.confirm')} className={'min-w-[200px]'}
                                     disabled={isEmpty(actionDetail) || isEmpty(actionTypeId) || !canEditContent}
                                     onClick={onClickProcessAction} />
                    </div>
                </>}
                {data?.alert?.confirm == BioAlertConfirmStatus.ASSIGN && !data?.alert?.isMyTask &&
                    <div className={'flex gap-[20px] justify-center'}>
                        <button className={'min-w-[200px] bg-sub_r min-h-[50px] text-[16px] px-[20px] text-white'}
                                disabled>{t('notification.bioModal.manager.assign')}
                        </button>
                    </div>}
                {data?.alert?.confirm == BioAlertConfirmStatus.DONE && <>
                    <div className={'flex gap-[8px] justify-center'}>
                        <SolidButton text={t('notification.bioModal.submit.save')} className={'min-w-[200px]'}
                                     onClick={onClickProcessAction} />
                    </div>
                </>}
            </div>
        </div>
    </div>
}

export default BioAlertDetail